import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

// Default sort configuration
export const DEFAULT_SORT = {
  field: 'createdAt',
  direction: 'desc'
};

// Create context
const SortingContext = createContext();

/**
 * Provider component for sorting state management
 */
export const SortingProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize sort state from URL or defaults
  const [sortConfig, setSortConfig] = useState(() => {
    const sortField = searchParams.get('sortField') || DEFAULT_SORT.field;
    const sortDirection = searchParams.get('sortDirection') || DEFAULT_SORT.direction;
    
    return {
      field: sortField,
      direction: sortDirection
    };
  });

  // Handle sort request
  const handleSortRequest = useCallback((field) => {
    setSortConfig((prevConfig) => {
      // Special case for createdAt (Created column) - only two states
      if (field === 'createdAt') {
        // If already sorting by createdAt, toggle direction
        if (prevConfig.field === 'createdAt') {
          return {
            field,
            direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
          };
        }
        // If not currently sorting by createdAt, start with descending
        return {
          field,
          direction: 'desc'
        };
      }
      
      // For other columns, use the three-state cycle
      if (prevConfig.field !== field) {
        // New field, start with ascending
        return {
          field,
          direction: 'asc'
        };
      } else if (prevConfig.direction === 'asc') {
        // Same field, currently asc, switch to desc
        return {
          field,
          direction: 'desc'
        };
      } else {
        // Same field, currently desc, reset to default
        return { ...DEFAULT_SORT };
      }
    });
  }, []);

  // Update URL when sort config changes
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sortField', sortConfig.field);
    newParams.set('sortDirection', sortConfig.direction);
    setSearchParams(newParams, { replace: true });
  }, [sortConfig, searchParams, setSearchParams]);

  // Memoize context value
  const contextValue = useMemo(() => ({
    sortConfig,
    handleSortRequest,
  }), [sortConfig, handleSortRequest]);

  return (
    <SortingContext.Provider value={contextValue}>
      {children}
    </SortingContext.Provider>
  );
};

// Custom hook to use the sorting context
export const useSorting = () => {
  const context = useContext(SortingContext);
  if (!context) {
    throw new Error('useSorting must be used within a SortingProvider');
  }
  return context;
};

export default SortingContext; 